import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from './ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { useToast } from '../components/ui/use-toast';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { FUNCTION_NAMES } from '../constants';
import { fetchWithRetry } from '../utils/tourUtils';
import { 
  Clock, 
  CheckCircle, 
  XCircle, 
  Camera, 
  Smartphone,
  Play,
  AlertCircle,
  Activity,
  TrendingUp,
  Database
} from 'lucide-react';

const Dashboard = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState({
    pushQueue: {
      pending: 0,
      completed: 0,
      failed: 0,
      totalAttachments: 0
    },
    syncTours: {
      pending: 0,
      synced: 0,
      ready: 0,
      tourGenerated: 0,
      failed: 0
    },
    tours: {
      total: 0,
      active: 0
    },
    recent: {
      pushQueueItems: [],
      syncTourItems: []
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch Push Queue data
      const [pushQueueAll, syncToursAll, toursAll] = await Promise.all([
        fetchWithRetry({
          url: `${API_BASE_URL}/api/post`,
          data: {
            function_name: FUNCTION_NAMES.PRISMA,
            operation: 'pushQueue.findMany',
            payload: { orderBy: { createdAt: 'desc' }, take: 100 }
          },
          label: 'dashboard push queue'
        }),
        fetchWithRetry({
          url: `${API_BASE_URL}/api/post`,
          data: {
            function_name: FUNCTION_NAMES.PRISMA,
            operation: 'syncTour.findMany',
            payload: { orderBy: { createdAt: 'desc' }, take: 100 }
          },
          label: 'dashboard sync tours'
        }),
        fetchWithRetry({
          url: `${API_BASE_URL}/api/post`,
          data: {
            function_name: FUNCTION_NAMES.PRISMA,
            operation: 'tour.findMany',
            payload: { take: 50 }
          },
          label: 'dashboard tours'
        })
      ]);

      // Process Push Queue stats
      const pushQueueStats = {
        pending: pushQueueAll.filter(item => item.pushStatus === 'PENDING').length,
        completed: pushQueueAll.filter(item => item.pushStatus === 'PUSHED').length,
        failed: pushQueueAll.filter(item => item.pushStatus === 'FAILED').length,
        totalAttachments: pushQueueAll.filter(item => item.changeType === 'ATTACHMENT_ADDED').length
      };

      // Process SyncTour stats
      const syncTourStats = {
        pending: syncToursAll.filter(item => item.syncStatus === 'PENDING').length,
        synced: syncToursAll.filter(item => item.syncStatus === 'SYNCED').length,
        ready: syncToursAll.filter(item => item.syncStatus === 'READY').length,
        tourGenerated: syncToursAll.filter(item => item.tourGenerated).length,
        failed: syncToursAll.filter(item => item.syncStatus === 'FAILED').length
      };

      // Process Tours stats
      const tourStats = {
        total: toursAll.length,
        active: toursAll.filter(tour => tour.isActivePublic).length
      };

      setDashboardData({
        pushQueue: pushQueueStats,
        syncTours: syncTourStats,
        tours: tourStats,
        recent: {
          pushQueueItems: pushQueueAll.slice(0, 5),
          syncTourItems: syncToursAll.slice(0, 5)
        }
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load dashboard data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('he-IL', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status, type = 'push') => {
    if (type === 'push') {
      const statusConfig = {
        PENDING: { variant: 'secondary', text: 'Pending' },
        PUSHED: { variant: 'default', text: 'Success', className: 'bg-green-100 text-green-800' },
        FAILED: { variant: 'destructive', text: 'Failed' }
      };
      const config = statusConfig[status] || statusConfig.PENDING;
      return <Badge variant={config.variant} className={config.className}>{config.text}</Badge>;
    } else {
      const statusConfig = {
        PENDING: { variant: 'secondary', text: 'Pending' },
        SYNCED: { variant: 'default', text: 'Synced', className: 'bg-blue-100 text-blue-800' },
        READY: { variant: 'default', text: 'Ready', className: 'bg-green-100 text-green-800' },
        FAILED: { variant: 'destructive', text: 'Failed' }
      };
      const config = statusConfig[status] || statusConfig.PENDING;
      return <Badge variant={config.variant} className={config.className}>{config.text}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center h-64">
          <Activity className="w-8 h-8 animate-spin" />
          <span className="ml-2">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  const pushQueueTotal = dashboardData.pushQueue.pending + dashboardData.pushQueue.completed + dashboardData.pushQueue.failed;
  const pushQueueSuccessRate = pushQueueTotal > 0 ? Math.round((dashboardData.pushQueue.completed / pushQueueTotal) * 100) : 0;

  const syncTourTotal = dashboardData.syncTours.pending + dashboardData.syncTours.synced + dashboardData.syncTours.ready + dashboardData.syncTours.failed;
  const syncTourSuccessRate = syncTourTotal > 0 ? Math.round(((dashboardData.syncTours.synced + dashboardData.syncTours.ready) / syncTourTotal) * 100) : 0;

  return (
    <div className="container mx-auto py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Dashboard</h1>
      </div>

      {/* Main Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">

        {/* Tours Overview */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <Play className="w-5 h-5 text-green-600" />
              Virtual Tours
            </CardTitle>
            <CardDescription>Tour management status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <p className="text-3xl font-bold">{dashboardData.tours.total}</p>
              <p className="text-sm text-gray-600 mb-2">Total Tours</p>
              <div className="flex justify-center">
                <Badge className="bg-green-100 text-green-800">
                  {dashboardData.tours.active} Active
                </Badge>
              </div>
            </div>
            <Progress 
              value={dashboardData.tours.total > 0 ? (dashboardData.tours.active / dashboardData.tours.total) * 100 : 0} 
              className="mt-4" 
            />
          </CardContent>
          <CardFooter>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => navigate('/tour-explorer')}
            >
              <Play className="w-4 h-4 mr-2" />
              Explore Tours
            </Button>
          </CardFooter>
        </Card>

        {/* Attachments */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-orange-600" />
              Attachments
            </CardTitle>
            <CardDescription>Media files from Android</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <p className="text-3xl font-bold">{dashboardData.pushQueue.totalAttachments}</p>
              <p className="text-sm text-gray-600">Total Attachments</p>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4">
              <div className="text-center p-2 bg-blue-50 rounded">
                <Camera className="w-4 h-4 mx-auto mb-1 text-blue-600" />
                <p className="text-xs">360° Images</p>
              </div>
              <div className="text-center p-2 bg-orange-50 rounded">
                <Smartphone className="w-4 h-4 mx-auto mb-1 text-orange-600" />
                <p className="text-xs">Tablet Images</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Push Queue Items */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Recent Push Queue Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData.recent.pushQueueItems.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No recent activity</p>
            ) : (
              <div className="space-y-3">
                {dashboardData.recent.pushQueueItems.map(item => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                      <div>
                        <p className="text-sm font-medium">
                          {item.changeType.replace('_', ' ')}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(item.createdAt)} • User: {item.userId}
                        </p>
                      </div>
                    </div>
                    {getStatusBadge(item.pushStatus, 'push')}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Sync Tour Items */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              Recent 360° Sync Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboardData.recent.syncTourItems.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No recent activity</p>
            ) : (
              <div className="space-y-3">
                {dashboardData.recent.syncTourItems.map(item => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                      <div>
                        <p className="text-sm font-medium">{item.spaceName}</p>
                        <p className="text-xs text-gray-500">
                          {formatDate(item.captureDate)} • User: {item.userId}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.tourGenerated && (
                        <Badge className="bg-green-100 text-green-800 text-xs">
                          <Play className="w-3 h-3 mr-1" />
                          Tour
                        </Badge>
                      )}
                      {getStatusBadge(item.syncStatus, 'sync')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      {(dashboardData.pushQueue.pending > 0 || dashboardData.syncTours.pending > 0) && (
        <Card className="mt-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-6 h-6 text-orange-500" />
                <div>
                  <h3 className="font-medium">Action Required</h3>
                  <p className="text-sm text-gray-600">
                    You have pending items that need attention
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                {dashboardData.pushQueue.pending > 0 && (
                  <Button onClick={() => navigate('/push-queue')}>
                    Review {dashboardData.pushQueue.pending} Push Queue Items
                  </Button>
                )}
                {dashboardData.syncTours.pending > 0 && (
                  <Button variant="outline" onClick={() => navigate('/sync-tours')}>
                    Process {dashboardData.syncTours.pending} Sync Tours
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
